/**
 * Context Panel Component
 * Shows context-aware quick actions based on current document/screen
 * Helps users understand "What can I do next?"
 */

import React from 'react';
import { Card, Space, Button, Tag, Typography, Divider, Alert } from 'antd';
import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  ToolOutlined,
  SafetyCertificateOutlined,
  SendOutlined,
  PrinterOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './ContextPanel.css';

const { Text, Title } = Typography;

interface QuickAction {
  key: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
  type?: 'primary' | 'default' | 'dashed';
  disabled?: boolean;
  badge?: string;
}

interface ContextPanelProps {
  documentType: 'sales-order' | 'work-order' | 'job-card' | 'inspection' | 'material-request' | 'quotation';
  documentId?: string;
  documentStatus?: string;
  documentData?: any;
}

export const ContextPanel: React.FC<ContextPanelProps> = ({
  documentType,
  documentId,
  documentStatus,
  documentData,
}) => {
  const navigate = useNavigate();

  // Get context-aware actions based on document type and status
  const getQuickActions = (): QuickAction[] => {
    switch (documentType) {
      case 'sales-order':
        return getSalesOrderActions();
      case 'work-order':
        return getWorkOrderActions();
      case 'job-card':
        return getJobCardActions();
      case 'inspection':
        return getInspectionActions();
      case 'material-request':
        return getMaterialRequestActions();
      case 'quotation':
        return getQuotationActions();
      default:
        return [];
    }
  };

  const getSalesOrderActions = (): QuickAction[] => {
    const actions: QuickAction[] = [];

    if (documentStatus === 'Draft') {
      actions.push({
        key: 'submit-so',
        label: 'Submit for Approval',
        description: 'Submit customer order for approval',
        icon: <SendOutlined />,
        onClick: () => console.log('Submit SO'),
        type: 'primary',
      });
    }

    if (documentStatus === 'Approved') {
      actions.push({
        key: 'create-wo',
        label: 'Create Work Order',
        description: 'Start production from this customer order',
        icon: <ToolOutlined />,
        onClick: () => navigate(`/mfg/work-orders/new?sales_order_id=${documentId}`),
        type: 'primary',
      });
    }

    actions.push({
      key: 'view-wo',
      label: 'View Related Work Orders',
      description: 'See all work orders for this customer order',
      icon: <LinkOutlined />,
      onClick: () => navigate(`/mfg/work-orders?sales_order_id=${documentId}`),
    });

    actions.push({
      key: 'print-so',
      label: 'Print Customer Order',
      description: 'Generate PDF document',
      icon: <PrinterOutlined />,
      onClick: () => console.log('Print SO'),
    });

    return actions;
  };

  const getWorkOrderActions = (): QuickAction[] => {
    const actions: QuickAction[] = [];

    if (documentStatus === 'Draft' || documentStatus === 'Not Started') {
      actions.push({
        key: 'start-wo',
        label: 'Start Production',
        description: 'Begin work on this work order',
        icon: <ArrowRightOutlined />,
        onClick: () => console.log('Start WO'),
        type: 'primary',
      });
    }

    if (documentStatus === 'In Progress') {
      actions.push({
        key: 'create-job-card',
        label: 'Create Job Card',
        description: 'Create operation-level job card',
        icon: <FileTextOutlined />,
        onClick: () => navigate(`/manufacturing/job-cards/create?work_order_id=${documentId}`),
        type: 'primary',
      });

    }

    if (documentStatus === 'Completed') {
      actions.push({
        key: 'create-inspection',
        label: 'Quality Inspection',
        description: 'Create quality inspection record',
        icon: <SafetyCertificateOutlined />,
        onClick: () => navigate(`/qms/inspections/new?work_order_id=${documentId}`),
        type: 'primary',
      });
    }

    actions.push({
      key: 'view-job-cards',
      label: 'View Job Cards',
      description: 'See all job cards for this work order',
      icon: <LinkOutlined />,
      onClick: () => navigate(`/manufacturing/job-cards?work_order_id=${documentId}`),
    });

    return actions;
  };

  const getJobCardActions = (): QuickAction[] => {
    const actions: QuickAction[] = [];

    if (documentStatus === 'Open') {
      actions.push({
        key: 'start-job',
        label: 'Start Job',
        description: 'Begin work on this operation',
        icon: <ArrowRightOutlined />,
        onClick: () => console.log('Start Job'),
        type: 'primary',
      });
    }

    if (documentStatus === 'In Progress') {
      actions.push({
        key: 'complete-job',
        label: 'Complete Job',
        description: 'Mark job as completed',
        icon: <CheckCircleOutlined />,
        onClick: () => console.log('Complete Job'),
        type: 'primary',
      });
    }

    actions.push({
      key: 'view-wo',
      label: 'View Parent Work Order',
      description: 'Go to work order',
      icon: <LinkOutlined />,
      onClick: () => navigate(`/mfg/work-orders/${documentData?.work_order_id}`),
    });

    return actions;
  };

  const getInspectionActions = (): QuickAction[] => {
    const actions: QuickAction[] = [];

    if (documentStatus === 'Pending') {
      actions.push({
        key: 'start-inspection',
        label: 'Start Inspection',
        description: 'Begin quality inspection',
        icon: <ArrowRightOutlined />,
        onClick: () => console.log('Start Inspection'),
        type: 'primary',
      });
    }

    if (documentStatus === 'In Progress') {
      actions.push({
        key: 'complete-inspection',
        label: 'Complete Inspection',
        description: 'Submit inspection results',
        icon: <CheckCircleOutlined />,
        onClick: () => console.log('Complete Inspection'),
        type: 'primary',
      });
    }

    actions.push({
      key: 'view-wo',
      label: 'View Work Order',
      description: 'Go to related work order',
      icon: <LinkOutlined />,
      onClick: () => navigate(`/mfg/work-orders/${documentData?.work_order_id}`),
    });

    return actions;
  };

  const getMaterialRequestActions = (): QuickAction[] => {
    const actions: QuickAction[] = [];

    if (documentStatus === 'Draft') {
      actions.push({
        key: 'submit-mr',
        label: 'Submit Request',
        description: 'Submit for approval',
        icon: <SendOutlined />,
        onClick: () => console.log('Submit MR'),
        type: 'primary',
      });
    }

    if (documentStatus === 'Approved') {
      actions.push({
        key: 'issue-materials',
        label: 'Issue Materials',
        description: 'Issue requested materials',
        icon: <SendOutlined />,
        onClick: () => navigate(`/scm/inventory/material-issues?material_request_id=${documentId}`),
        type: 'primary',
      });
    }

    return actions;
  };

  const getQuotationActions = (): QuickAction[] => {
    const actions: QuickAction[] = [];

    if (documentStatus === 'Draft') {
      actions.push({
        key: 'submit-quote',
        label: 'Send to Customer',
        description: 'Email quotation to customer',
        icon: <SendOutlined />,
        onClick: () => console.log('Send Quote'),
        type: 'primary',
      });
    }

    if (documentStatus === 'Accepted') {
      actions.push({
        key: 'create-so',
        label: 'Create Customer Order',
        description: 'Convert quotation to customer order',
        icon: <FileTextOutlined />,
        onClick: () => navigate(`/growth/orders/new?quotation_id=${documentId}`),
        type: 'primary',
      });
    }

    actions.push({
      key: 'print-quote',
      label: 'Print Quotation',
      description: 'Generate PDF document',
      icon: <PrinterOutlined />,
      onClick: () => console.log('Print Quote'),
    });

    return actions;
  };

  const actions = getQuickActions();

  if (actions.length === 0) {
    return null;
  }

  return (
    <Card 
      className="context-panel"
      title={
        <Space>
          <Text strong>🎯 What's Next?</Text>
          {documentStatus && (
            <Tag color="blue">{documentStatus}</Tag>
          )}
        </Space>
      }
      size="small"
    >
      <Alert
        message="Quick Actions"
        description="Recommended next steps for this document"
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {actions.map((action) => (
          <Button
            key={action.key}
            type={action.type || 'default'}
            icon={action.icon}
            onClick={action.onClick}
            disabled={action.disabled}
            block
            size="large"
            style={{ 
              textAlign: 'left', 
              height: 'auto',
              padding: '12px 16px'
            }}
          >
            <div>
              <div style={{ fontWeight: 600 }}>{action.label}</div>
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>
                {action.description}
              </div>
            </div>
          </Button>
        ))}
      </Space>

      <Divider style={{ margin: '16px 0' }} />

      <Text type="secondary" style={{ fontSize: 12 }}>
        💡 Tip: These actions are based on your current document status
      </Text>
    </Card>
  );
};

export default ContextPanel;
